import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fetchCurrentUser } from '@/features/auth/api/getCurrentUser';
import { useQuery } from '@tanstack/react-query';
import { useResetPassword } from '../../api/resetPassword';
import { User } from '../../types';

interface ResetPasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

const ResetPasswordDialog: React.FC<ResetPasswordDialogProps> = ({ open, onClose }) => {
  const {
    data: user,
    isLoading: loadingUser,
    error: userError,
  } = useQuery<User | null>({
    queryKey: ['current-user'],
    queryFn: fetchCurrentUser,
  });

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const resetPasswordMutation = useResetPassword();

  // Fungsi untuk memeriksa kekuatan password
  const isPasswordStrong = (password: string): boolean => {
    const minLength = 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    // const hasSpecialChar = /[!@#$%^&*]/.test(password);
    
    return password.length >= minLength && hasUppercase && hasLowercase && hasNumbers;
  };

  if (loadingUser) return <div>Loading user data...</div>;

  if (userError) {
    return <div>Error loading user data: {userError.message}</div>;
  }

  const handleSend = async () => {
    setError('');
    setLoading(true);

    if (!user?.profile_data?.id) {
      setError('User tidak ditemukan'); 
      setLoading(false);
      return;
    }

    try {
      await resetPasswordMutation.mutateAsync({
        userId: String(user.profile_data.id),
        password: newPassword,
        password_confirmation: confirmPassword,
      });

      setOpenSnackbar(true);
      handleClose();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Terjadi kesalahan saat mereset kata sandi.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    onClose();
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const isSubmitDisabled = !isPasswordStrong(newPassword) || newPassword !== confirmPassword;

  return (
    <>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>
          Reset Password
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="new-password"
            label="Kata Sandi Baru"
            type="password"
            fullWidth
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <TextField
            margin="dense"
            id="confirm-password"
            label="Konfirmasi Kata Sandi"
            type="password"
            fullWidth
            variant="outlined"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
          {!isPasswordStrong(newPassword) && (
            <Typography color="error" variant="body2">
              Password harus minimal 8 karakter, termasuk huruf besar, huruf kecil dan angka
            </Typography>
          )}
          {newPassword !== confirmPassword && (
            <Typography color="error" variant="body2">
              Konfirmasi kata sandi harus sama dengan kata sandi baru.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Batal</Button>
          <Button 
            variant="contained" 
            onClick={handleSend} 
            disabled={isSubmitDisabled}
          >
            {loading ? <CircularProgress size={24} /> : 'Kirim'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message="Kata sandi telah berhasil direset."
      />
    </>
  );
};

export default ResetPasswordDialog;